import type { UserClientFrame, UserServerFrame, WsConnectionState } from '@talkvibe/shared';

import { api } from './api.ts';
import { ReconnectingSocket } from './reconnecting-socket.ts';

/**
 * The socket that stays open for the whole session.
 *
 * One per signed-in client, independent of which page is showing. It is what
 * makes presence mean "has the app open" rather than "has this one chat open",
 * and it is how a notification or a message in a conversation you are not
 * looking at reaches you without polling for it.
 */

interface TicketResponse {
  ticket: string;
  expiresAt: string;
  url: string;
}

export interface UserChannelHandlers {
  onFrame: (frame: UserServerFrame) => void;
  onStateChange: (state: WsConnectionState) => void;
}

export class UserChannelConnection extends ReconnectingSocket<UserClientFrame> {
  /**
   * People whose presence this client cares about. Kept so the set can be
   * re-sent after a reconnect — the server holds no subscription across a
   * dropped socket, and a silently empty list would leave every dot grey.
   */
  private readonly watched = new Set<string>();

  constructor(private readonly handlers: UserChannelHandlers) {
    super(handlers.onStateChange);
  }

  protected async ticketUrl(): Promise<string> {
    const ticket = await api.post<TicketResponse>('/api/realtime/user-ticket');
    return ticket.url;
  }

  protected handleFrame(frame: unknown): void {
    const typed = frame as UserServerFrame;
    if (typeof typed?.type !== 'string') return;
    if (typed.type === 'pong') return;
    this.handlers.onFrame(typed);
  }

  protected heartbeat(): UserClientFrame {
    return { type: 'ping' };
  }

  protected override onOpen(): void {
    if (this.watched.size > 0) this.send({ type: 'watch', payload: { userIds: [...this.watched] } });
  }

  /** Asks for presence on these people, now and after every reconnect. */
  watch(userIds: string[]): void {
    const added = userIds.filter((id) => !this.watched.has(id));
    for (const id of userIds) this.watched.add(id);
    // Re-asking for people already watched would cost a round trip to learn
    // what we already know.
    if (added.length > 0) this.send({ type: 'watch', payload: { userIds: added } });
  }
}
